import { useMemo, useRef, useState } from 'react'
import type { Taak } from '../../../gedeeld/types'
import { api } from '../../lib/api'
import { isTeLaat, sleutelVan, startVanDag } from '../../lib/opmaak'
import { meldFout, openTaak, taakGewijzigd, useBasis } from '../../lib/staat'
import { Kies, Leeg } from '../ui'
import { SoortIcoon } from '../taak/stukjes'
import type { ProjectContext } from '../ProjectScherm'

/* ===========================================================================
 *  De tijdlijn
 *
 *  Een balk van start tot deadline, gegroepeerd per epic. Slepen schuift de
 *  hele balk, de rechterrand trekken verzet alleen de deadline. Wat geen
 *  datum heeft staat er niet op -- een balk die op de aanmaakdatum begint en
 *  "ergens" eindigt, suggereert een planning die er niet is. Onderaan staat
 *  hoeveel taken daardoor ontbreken, zodat het geen stille gaten zijn.
 * =========================================================================== */

type Schaal = 'week' | 'maand' | 'kwartaal'
const DAG = 86400000
const BREEDTE: Record<Schaal, number> = { week: 44, maand: 18, kwartaal: 7 }

interface Balk { taak: Taak; van: number; tot: number }

export default function Tijdlijn({ ctx }: { ctx: ProjectContext }) {
  const { projecten } = useBasis()
  const [schaal, setSchaal] = useState<Schaal>('maand')
  const [sleep, setSleep] = useState<{ id: string; soort: 'schuif' | 'rek'; x0: number; dagen: number } | null>(null)
  const baan = useRef<HTMLDivElement>(null)
  const px = BREEDTE[schaal]

  const balken = useMemo<Balk[]>(() => ctx.zicht
    .filter((t) => t.deadline || t.startAt)
    .map((t) => {
      const tot = startVanDag(t.deadline ?? (t.startAt! + 2 * DAG))
      const van = startVanDag(t.startAt ?? Math.min(tot, t.createdAt))
      return { taak: t, van: Math.min(van, tot), tot }
    }), [ctx.zicht])

  const zonderDatum = ctx.zicht.filter((t) => !t.deadline && !t.startAt && t.status !== 'klaar').length

  const vandaag = startVanDag(Date.now())
  const begin = Math.min(vandaag - 14 * DAG, ...balken.map((b) => b.van)) - 3 * DAG
  const eind = Math.max(vandaag + 45 * DAG, ...balken.map((b) => b.tot)) + 7 * DAG
  const dagen = Math.round((eind - begin) / DAG)

  /* Per epic, met de epic zelf als eerste regel. */
  const groepen = useMemo(() => {
    const epics = ctx.taken.filter((t) => t.soort === 'epic')
    const uit: { titel: string; epic: Taak | null; balken: Balk[] }[] = []
    for (const e of epics) {
      const eigen = balken.filter((b) => b.taak.ouderId === e.id || b.taak.id === e.id)
      if (eigen.length) uit.push({ titel: e.titel, epic: e, balken: eigen.sort((a, b) => (a.taak.id === e.id ? -1 : b.taak.id === e.id ? 1 : a.van - b.van)) })
    }
    const los = balken.filter((b) => !epics.some((e) => e.id === b.taak.ouderId || e.id === b.taak.id))
    if (los.length) uit.push({ titel: epics.length ? 'Zonder epic' : '', epic: null, balken: los.sort((a, b) => a.van - b.van) })
    return uit
  }, [balken, ctx.taken])

  const x = (t: number) => ((t - begin) / DAG) * px

  /* De koppen: maanden boven, dagen of weken eronder. */
  const maanden: { links: number; breed: number; naam: string }[] = []
  {
    const d = new Date(begin)
    d.setDate(1)
    while (d.getTime() < eind) {
      const v = Math.max(begin, d.getTime())
      const n = new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime()
      maanden.push({ links: x(v), breed: x(Math.min(eind, n)) - x(v), naam: d.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' }) })
      d.setMonth(d.getMonth() + 1)
    }
  }

  async function laatLos(b: Balk, delta: number, soort: 'schuif' | 'rek') {
    if (!delta) return
    const deel: Partial<Taak> = soort === 'schuif'
      ? { startAt: b.taak.startAt ? b.taak.startAt + delta * DAG : b.van + delta * DAG + 12 * 3600000, deadline: (b.taak.deadline ?? b.tot) + delta * DAG }
      : { deadline: Math.max(b.van, (b.taak.deadline ?? b.tot) + delta * DAG) }
    const voor = ctx.taken
    ctx.setTaken((l) => l.map((t) => (t.id === b.taak.id ? { ...t, ...deel } : t)))
    try {
      taakGewijzigd(await api.taakOpslaan({ id: b.taak.id, ...deel }))
    } catch (e) {
      ctx.setTaken(() => voor)
      meldFout(e)
    }
  }

  if (!balken.length) {
    return (
      <Leeg titel="Nog niets op de tijdlijn"
        tekst={`Een taak komt hierop zodra hij een start of een deadline heeft.${zonderDatum ? ` Er zijn ${zonderDatum} open taken zonder datum.` : ''}`} />
    )
  }

  return (
    <div className="tijdlijn-wrap">
      <div className="werkbalk dun">
        <span className="stil">Schaal</span>
        <Kies<Schaal> klein waarde={schaal} onKies={setSchaal} opties={[
          { key: 'week', label: 'Dagen' }, { key: 'maand', label: 'Weken' }, { key: 'kwartaal', label: 'Maanden' },
        ]} />
        <span className="vul" />
        {zonderDatum > 0 && <span className="stil">{zonderDatum} open {zonderDatum === 1 ? 'taak staat' : 'taken staan'} er niet op: geen datum.</span>}
      </div>

      <div className="tijdlijn">
        <div className="tl-namen">
          <div className="tl-kop-ruimte" />
          {groepen.map((g) => (
            <div key={g.titel + (g.epic?.id ?? '')}>
              {g.titel && <div className="tl-groep">{g.titel}</div>}
              {g.balken.map((b) => (
                <button key={b.taak.id} type="button" className="tl-naam" onClick={() => openTaak(b.taak.id)}>
                  <SoortIcoon soort={b.taak.soort} grootte={12} />
                  <span className="mono stil">{sleutelVan(b.taak, projecten)}</span>
                  <span className="afkap">{b.taak.titel}</span>
                </button>
              ))}
            </div>
          ))}
        </div>

        <div className="tl-rooster" ref={baan}
          onPointerMove={(e) => {
            if (!sleep) return
            const d = Math.round((e.clientX - sleep.x0) / px)
            if (d !== sleep.dagen) setSleep({ ...sleep, dagen: d })
          }}
          onPointerUp={() => {
            if (!sleep) return
            const b = balken.find((x) => x.taak.id === sleep.id)
            const s = sleep
            setSleep(null)
            if (b) void laatLos(b, s.dagen, s.soort)
          }}>
          <div className="tl-binnen" style={{ width: dagen * px }}>
            <div className="tl-kop">
              {maanden.map((m) => <div key={m.links} className="tl-maand" style={{ left: m.links, width: m.breed }}>{m.naam}</div>)}
              {schaal === 'week' && Array.from({ length: dagen }, (_, i) => {
                const d = new Date(begin + i * DAG)
                return <div key={i} className={`tl-dag ${[0, 6].includes(d.getDay()) ? 'weekend' : ''}`} style={{ left: i * px, width: px }}>{d.getDate()}</div>
              })}
              {schaal === 'maand' && Array.from({ length: dagen }, (_, i) => i).filter((i) => new Date(begin + i * DAG).getDay() === 1).map((i) => (
                <div key={i} className="tl-dag" style={{ left: i * px, width: px * 7 }}>{new Date(begin + i * DAG).getDate()}</div>
              ))}
            </div>
            <div className="tl-vandaag" style={{ left: x(vandaag) + px / 2 }} title="Vandaag" />
            {groepen.map((g) => (
              <div key={g.titel + (g.epic?.id ?? '')}>
                {g.titel && <div className="tl-groep leeg" />}
                {g.balken.map((b) => {
                  const actief = sleep?.id === b.taak.id
                  const verschuif = actief && sleep!.soort === 'schuif' ? sleep!.dagen : 0
                  const rek = actief && sleep!.soort === 'rek' ? sleep!.dagen : 0
                  const links = x(b.van) + verschuif * px
                  const breed = Math.max(px, x(b.tot + DAG) - x(b.van) + rek * px)
                  return (
                    <div key={b.taak.id} className="tl-regel">
                      <div
                        className={`tl-balk t-${b.taak.soort === 'epic' ? 'paars' : b.taak.status === 'klaar' ? 'ok' : isTeLaat(b.taak) ? 'danger' : b.taak.status === 'bezig' ? 'brand' : 'info'} ${actief ? 'sleept' : ''}`}
                        style={{ left: links, width: breed }}
                        title={`${b.taak.titel}\n${new Date(b.van).toLocaleDateString('nl-NL')} – ${new Date(b.tot).toLocaleDateString('nl-NL')}`}
                        onPointerDown={(e) => {
                          if ((e.target as HTMLElement).classList.contains('tl-rek')) return
                          e.currentTarget.setPointerCapture(e.pointerId)
                          setSleep({ id: b.taak.id, soort: 'schuif', x0: e.clientX, dagen: 0 })
                        }}
                        onDoubleClick={() => openTaak(b.taak.id)}>
                        <span className="tl-balk-tekst">{b.taak.titel}</span>
                        {b.taak.toegewezenNaam && <span className="tl-balk-wie">{b.taak.toegewezenNaam.split(' ')[0]}</span>}
                        <span className="tl-rek" onPointerDown={(e) => {
                          e.stopPropagation()
                          ;(e.currentTarget.parentElement as HTMLElement).setPointerCapture(e.pointerId)
                          setSleep({ id: b.taak.id, soort: 'rek', x0: e.clientX, dagen: 0 })
                        }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      <p className="stil klein-tekst">Sleep een balk om hem te verschuiven, trek aan de rechterrand voor een andere deadline. Dubbelklik opent de taak.</p>
    </div>
  )
}
